import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../lib/instant";
import { useCart } from "../context/CartContext";
import { ShoppingCart, Star, ChevronRight, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import Reviews from "../components/Reviews";

export default function ProductDetail({ theme }) {
  const { id } = useParams();
  const { addToCart } = useCart();
  const [qty, setQty]   = useState(1);
  const [tab, setTab]   = useState("description");
  const [added, setAdded] = useState(false);

  const primaryColor   = theme?.primaryColor   || "#6b2118";
  const accentColor    = theme?.accentColor    || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";

  const { data, isLoading } = db.useQuery({ products: { $: { where: { id } } } });
  const product = data?.products?.[0];

  // Still loading
  if (isLoading) return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#fdf6e3", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: "2.5rem" }}>⏳</div>
      <p style={{ color: "#aaa" }}>Loading product...</p>
    </div>
  );

  // Not found
  if (!product) return (
    <div style={{ paddingTop: 64, minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, background: "#fdf6e3" }}>
      <div style={{ fontSize: "3rem" }}>🌾</div>
      <h2 style={{ fontFamily: "Georgia, serif", color: "#2c1810" }}>Product Not Found</h2>
      <p style={{ color: "#aaa", fontSize: "0.9rem" }}>This product may have been removed or doesn't exist.</p>
      <Link to="/shop" style={{ background: primaryColor, color: "#fff", padding: "11px 28px", borderRadius: 40, textDecoration: "none", fontWeight: 700 }}>← Back to Shop</Link>
    </div>
  );

  const handleAddToCart = () => {
    addToCart(product, qty);
    setAdded(true);
    toast.success(`${product.name} added to cart!`);
    setTimeout(() => setAdded(false), 2000);
  };

  const tabs = [
    { key: "description", label: "Description" },
    { key: "ingredients", label: "Ingredients" },
    { key: "howto", label: "How to Use" },
  ];

  return (
    <div style={{ paddingTop: 64, minHeight: "100vh", background: "#fdf6e3" }}>
      {/* Breadcrumb */}
      <div style={{ background: "#fff", padding: "12px 5vw", borderBottom: "1px solid #f0e8d0" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 8, fontSize: "0.8rem", color: "#999" }}>
          <Link to="/" style={{ color: "#999", textDecoration: "none" }}>Home</Link>
          <ChevronRight size={14} />
          <Link to="/shop" style={{ color: "#999", textDecoration: "none" }}>Shop</Link>
          <ChevronRight size={14} />
          <span style={{ color: primaryColor }}>{product.name}</span>
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 5vw" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "start" }}>
          {/* Image */}
          <div style={{
            background: "#fff", borderRadius: 20, aspectRatio: "1",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 40px rgba(0,0,0,0.1)", position: "relative",
            overflow: "hidden",
          }}>
            {product.imageUrl ? (
              <img src={product.imageUrl} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: "6rem" }}>🌾</div>
                <div style={{ fontFamily: "Georgia, serif", fontSize: "1.2rem", color: primaryColor, marginTop: 8 }}>ACHU Health Mix</div>
                <div style={{ fontSize: "0.85rem", color: "#999" }}>{product.weight}</div>
              </div>
            )}
            {product.badge && (
              <div style={{ position: "absolute", top: 16, left: 16, background: primaryColor, color: "#fff", padding: "6px 14px", borderRadius: 20, fontSize: "0.75rem", fontWeight: 700 }}>{product.badge}</div>
            )}
          </div>

          {/* Info */}
          <div>
            <div style={{ fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: secondaryColor, fontWeight: 700, marginBottom: 8 }}>{product.weight} Pack</div>
            <h1 style={{ fontFamily: "Georgia, serif", fontSize: "2.2rem", color: "#2c1810", marginBottom: 12, lineHeight: 1.2 }}>{product.name}</h1>

            {product.rating > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
                <div style={{ display: "flex", gap: 3 }}>
                  {[1,2,3,4,5].map(s => <Star key={s} size={16} fill={s <= product.rating ? accentColor : "none"} color={accentColor} />)}
                </div>
                <span style={{ fontSize: "0.85rem", color: "#888" }}>{product.rating} ({product.reviewCount} reviews)</span>
              </div>
            )}

            <div style={{ marginBottom: 24 }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "2.5rem", fontWeight: 800, color: primaryColor }}>₹{product.price}</span>
              {product.originalPrice && <span style={{ fontSize: "1rem", color: "#bbb", textDecoration: "line-through", marginLeft: 12 }}>₹{product.originalPrice}</span>}
            </div>

            <p style={{ fontSize: "0.92rem", color: "#666", lineHeight: 1.7, marginBottom: 28 }}>{product.shortDesc}</p>

            {/* Badges */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 32 }}>
              {["100% Natural", "No Preservatives", "Zero Added Sugar", "27 Grains"].map(b => (
                <div key={b} style={{ display: "flex", alignItems: "center", gap: 6, background: "#f0faf0", padding: "6px 14px", borderRadius: 20, fontSize: "0.78rem", color: secondaryColor, fontWeight: 700, border: `1px solid ${secondaryColor}30` }}>
                  <CheckCircle size={12} /> {b}
                </div>
              ))}
            </div>

            {/* Quantity + Add */}
            <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", border: `2px solid ${primaryColor}30`, borderRadius: 12, overflow: "hidden" }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} style={{ width: 40, height: 44, background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", color: primaryColor }}>−</button>
                <span style={{ width: 40, textAlign: "center", fontWeight: 700, fontSize: "1rem" }}>{qty}</span>
                <button onClick={() => setQty(q => q + 1)} style={{ width: 40, height: 44, background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", color: primaryColor }}>+</button>
              </div>
              <button onClick={handleAddToCart} disabled={!product.inStock} style={{
                flex: 1, padding: "13px 24px",
                background: added ? secondaryColor : primaryColor,
                color: "#fff", border: "none", borderRadius: 12,
                fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                transition: "background 0.3s",
              }}>
                {added ? <><CheckCircle size={18} /> Added!</> : <><ShoppingCart size={18} /> Add to Cart</>}
              </button>
            </div>

            <Link to="/cart" style={{
              display: "block", textAlign: "center", padding: "13px 24px",
              border: `2px solid ${primaryColor}`, color: primaryColor,
              borderRadius: 12, fontWeight: 700, fontSize: "0.95rem",
              textDecoration: "none", marginBottom: 28,
            }}>View Cart & Checkout</Link>

            {/* Stock */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.83rem" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: product.inStock ? "#4caf50" : "#f44336" }} />
              <span style={{ color: product.inStock ? "#4caf50" : "#f44336", fontWeight: 700 }}>{product.inStock ? "In Stock" : "Out of Stock"}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginTop: 64 }}>
          <div style={{ display: "flex", borderBottom: `2px solid ${primaryColor}20`, marginBottom: 32, gap: 4 }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: "12px 24px", border: "none", background: "none",
                fontWeight: 700, fontSize: "0.9rem", cursor: "pointer",
                color: tab === t.key ? primaryColor : "#999",
                borderBottom: tab === t.key ? `3px solid ${primaryColor}` : "3px solid transparent",
                marginBottom: -2, transition: "all 0.2s",
              }}>{t.label}</button>
            ))}
          </div>

          <div style={{ background: "#fff", borderRadius: 16, padding: 32, fontSize: "0.92rem", color: "#555", lineHeight: 1.8 }}>
            {tab === "description" && <p style={{ whiteSpace: "pre-line" }}>{product.description || "100% Natural Multi Grain Health Mix with 27 grains. Hand-made with no preservatives, no added sugar, no artificial colour or flavour."}</p>}
            {tab === "ingredients" && (
              <div>
                <p style={{ marginBottom: 16 }}>Made from 27 carefully selected grains, millets, cereals, pulses and dry nuts:</p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {(product.ingredients || "Wheat, Rice, Ragi, Bajra, Jowar, Corn, Oats, Barley, Chickpeas, Green Gram, Black Gram, Horse Gram, Lentils, Soybean, Groundnuts, Almonds, Cashews, Cardamom, Dry Ginger, Turmeric, Flaxseeds, Sesame, Dates, Fenugreek, Cumin, Pepper, Dry Coconut").split(",").map(g => (
                    <span key={g.trim()} style={{ background: "#fdf6e3", border: `1px solid ${accentColor}50`, padding: "4px 12px", borderRadius: 20, fontSize: "0.8rem", color: "#5a3825" }}>{g.trim()}</span>
                  ))}
                </div>
              </div>
            )}
            {tab === "howto" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 24 }}>
                {[
                  { n: "1", t: "Measure", d: "Take 2–3 tbsp of Health Mix into a cup." },
                  { n: "2", t: "Mix", d: "Add warm water or milk, stir into a smooth paste." },
                  { n: "3", t: "Sweeten", d: "Add jaggery, honey or sweetener to taste." },
                  { n: "4", t: "Enjoy!", d: "Sip warm or chilled. Best consumed daily." },
                ].map(s => (
                  <div key={s.n} style={{ textAlign: "center", padding: 16 }}>
                    <div style={{ width: 56, height: 56, borderRadius: "50%", background: primaryColor, color: accentColor, fontFamily: "Georgia, serif", fontSize: "1.4rem", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>{s.n}</div>
                    <h4 style={{ color: primaryColor, marginBottom: 6 }}>{s.t}</h4>
                    <p style={{ color: "#777", fontSize: "0.83rem" }}>{s.d}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reviews */}
        <Reviews productId={id} theme={theme} />
      </div>

      <style>{`@media(max-width:768px){.product-grid{grid-template-columns:1fr!important;}}`}</style>
    </div>
  );
}
